import { useState, useEffect, useCallback, useRef } from "react";
import { Upload, CheckCircle, AlertCircle, Clock, FileSpreadsheet, Trash2, RefreshCw, ChevronDown } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";

const STATUS_CONFIG = {
  not_uploaded: { label: "Not uploaded", cls: "badge-muted", icon: null },
  processing: { label: "Processing", cls: "badge-warning", icon: Clock },
  awaiting_mapping: { label: "Awaiting column mapping", cls: "badge-warning", icon: Clock },
  processed: { label: "Processed — ready", cls: "badge-success", icon: CheckCircle },
  error: { label: "Error — see details", cls: "badge-danger", icon: AlertCircle },
};

const VECTORS = [
  { key: "electrical", titleKey: "energy_vec_electrical", descKey: "energy_vec_electrical_desc" },
  { key: "thermal", titleKey: "energy_vec_thermal", descKey: "energy_vec_thermal_desc" },
  { key: "gas", titleKey: "energy_vec_gas", descKey: "energy_vec_gas_desc" },
];

const EnergyDataUploadView = ({ clientId, assetUuid }) => {
  const t = useT();
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [expandedVector, setExpandedVector] = useState(null);
  const [vectorDetails, setVectorDetails] = useState({});
  const [mappingState, setMappingState] = useState({ month: "", value: "", year: "" });
  const fileInputRefs = useRef({});

  const aq = assetUuid ? `?asset_uuid=${assetUuid}` : "";
  const aqAmp = assetUuid ? `&asset_uuid=${assetUuid}` : "";

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/energy-data/${clientId}/status${aq}`);
      setStatuses(res.data);
    } catch (err) {
      console.error("Error fetching energy data status:", err);
    } finally {
      setLoading(false);
    }
  }, [clientId, aq]);

  useEffect(() => {
    setStatuses({});
    setVectorDetails({});
    setExpandedVector(null);
    setLoading(true);
    fetchStatuses();
  }, [fetchStatuses]);

  const handleUpload = async (vectorType, file) => {
    if (!file) return;
    setUploading((p) => ({ ...p, [vectorType]: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(
        `${API}/energy-data/${clientId}/upload/${vectorType}${aq}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      await fetchStatuses();
      if (res.data.needs_mapping) {
        setExpandedVector(vectorType);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setStatuses((p) => ({
        ...p,
        [vectorType]: { status: "error", error_detail: detail },
      }));
    } finally {
      setUploading((p) => ({ ...p, [vectorType]: false }));
      if (fileInputRefs.current[vectorType]) fileInputRefs.current[vectorType].value = "";
    }
  };

  const handleDelete = async (vectorType) => {
    if (!window.confirm("Remove this upload?")) return;
    try {
      await axios.delete(`${API}/energy-data/${clientId}/vector/${vectorType}${aq}`);
      setStatuses((p) => ({ ...p, [vectorType]: { status: "not_uploaded" } }));
      setVectorDetails((p) => { const n = { ...p }; delete n[vectorType]; return n; });
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleSaveMapping = async () => {
    if (!mappingState.month || !mappingState.value) return;
    try {
      const mapping = { month: mappingState.month, value: mappingState.value };
      if (mappingState.year) mapping.year = mappingState.year;
      await axios.post(
        `${API}/energy-data/${clientId}/thermal-mapping${aq}`,
        { column_mapping: mapping }
      );
      await fetchStatuses();
      setMappingState({ month: "", value: "", year: "" });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      alert("Mapping error: " + detail);
    }
  };

  const fetchVectorDetail = async (vectorType) => {
    try {
      const res = await axios.get(`${API}/energy-data/${clientId}/vector/${vectorType}${aq}`);
      setVectorDetails((p) => ({ ...p, [vectorType]: res.data }));
    } catch (err) {
      console.error("Fetch detail error:", err);
    }
  };

  const toggleExpand = (vectorType) => {
    if (expandedVector === vectorType) {
      setExpandedVector(null);
    } else {
      setExpandedVector(vectorType);
      const vs = statuses[vectorType];
      if (vs && vs.status !== "not_uploaded" && !vectorDetails[vectorType]) {
        fetchVectorDetail(vectorType);
      }
    }
  };

  const onDrop = (vectorType) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(vectorType, file);
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="view-container" data-testid="energy-data-upload-view">
      <div className="view-header">
        <div>
          <h2>{t("energy_data_upload")}</h2>
          <p className="text-muted">{t("energy_data_upload_desc")}</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={fetchStatuses} data-testid="energy-data-refresh">
          <RefreshCw size={16} /> {t("refresh")}
        </button>
      </div>

      <div className="energy-cards-grid">
        {VECTORS.map(({ key, titleKey, descKey }) => {
          const vs = statuses[key] || { status: "not_uploaded" };
          const cfg = STATUS_CONFIG[vs.status] || STATUS_CONFIG.not_uploaded;
          const isExpanded = expandedVector === key;
          const isUploading = uploading[key];
          const detail = vectorDetails[key];

          return (
            <div className="energy-upload-card" key={key} data-testid={`energy-card-${key}`}>
              <div className="energy-card-header" onClick={() => toggleExpand(key)}>
                <div className="energy-card-title-row">
                  <FileSpreadsheet size={20} />
                  <h3>{t(titleKey)}</h3>
                  <span className={`badge ${cfg.cls}`} data-testid={`energy-status-${key}`}>
                    {cfg.icon && <cfg.icon size={12} />} {cfg.label}
                  </span>
                </div>
                <p className="text-muted energy-card-desc">{t(descKey)}</p>
                {vs.filename && (
                  <div className="energy-card-meta">
                    <span>{vs.filename}</span>
                    {vs.uploaded_at && <span> — {new Date(vs.uploaded_at).toLocaleDateString()}</span>}
                    {vs.uploaded_by && <span> by {vs.uploaded_by}</span>}
                  </div>
                )}
                <ChevronDown size={16} className={`energy-expand-icon ${isExpanded ? "rotated" : ""}`} />
              </div>

              <div className={`energy-card-body ${isExpanded ? "expanded" : ""}`}>
                {/* Upload zone */}
                <div
                  className="energy-drop-zone"
                  onDrop={onDrop(key)}
                  onDragOver={onDragOver}
                  data-testid={`energy-drop-${key}`}
                >
                  <Upload size={24} />
                  <p>{isUploading ? "Uploading..." : "Drag & drop .xlsx file here or"}</p>
                  <label className="btn btn-primary btn-sm">
                    {vs.status !== "not_uploaded" ? "Replace File" : "Choose File"}
                    <input
                      type="file"
                      accept=".xlsx"
                      style={{ display: "none" }}
                      ref={(el) => (fileInputRefs.current[key] = el)}
                      onChange={(e) => handleUpload(key, e.target.files?.[0])}
                      disabled={isUploading}
                      data-testid={`energy-input-${key}`}
                    />
                  </label>
                </div>

                {/* Error detail */}
                {vs.status === "error" && vs.error_detail && (
                  <div className="alert alert-danger" style={{ marginTop: "12px", fontSize: "0.85rem" }}>
                    <AlertCircle size={16} />
                    <span>{vs.error_detail}</span>
                  </div>
                )}

                {/* Thermal mapping UI */}
                {key === "thermal" && vs.status === "awaiting_mapping" && vs.available_columns && (
                  <div className="energy-mapping-section" data-testid="thermal-mapping-ui">
                    <h4>{t("energy_thermal_mapping_title")}</h4>
                    <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: "8px" }}>
                      {t("energy_thermal_mapping_desc")}
                    </p>
                    {vs.preview_rows && vs.preview_rows.length > 0 && (
                      <div className="table-responsive" style={{ marginBottom: "12px", maxHeight: "160px", overflow: "auto" }}>
                        <table className="data-table" style={{ fontSize: "0.75rem" }}>
                          <thead>
                            <tr>{vs.available_columns.map((c) => <th key={c}>{c}</th>)}</tr>
                          </thead>
                          <tbody>
                            {vs.preview_rows.map((row, i) => (
                              <tr key={i}>{vs.available_columns.map((c) => <td key={c}>{row[c] ?? ""}</td>)}</tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="energy-mapping-fields">
                      <div className="form-group">
                        <label className="form-label">Month column *</label>
                        <select className="form-input" value={mappingState.month} onChange={(e) => setMappingState((p) => ({ ...p, month: e.target.value }))} data-testid="mapping-month-select">
                          <option value="">-- Select --</option>
                          {vs.available_columns.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Value column (kWh/m³) *</label>
                        <select className="form-input" value={mappingState.value} onChange={(e) => setMappingState((p) => ({ ...p, value: e.target.value }))} data-testid="mapping-value-select">
                          <option value="">-- Select --</option>
                          {vs.available_columns.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Year column (optional)</label>
                        <select className="form-input" value={mappingState.year} onChange={(e) => setMappingState((p) => ({ ...p, year: e.target.value }))} data-testid="mapping-year-select">
                          <option value="">-- None --</option>
                          {vs.available_columns.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveMapping} disabled={!mappingState.month || !mappingState.value} data-testid="mapping-save-btn">
                      Confirm Mapping & Process
                    </button>
                  </div>
                )}

                {/* Reconciliation display for electrical/gas */}
                {vs.reconciliation && (key === "electrical" || key === "gas") && (
                  <div className="energy-reconciliation" data-testid={`reconciliation-${key}`}>
                    <h4>Calendar Normalization Reconciliation</h4>
                    <div className="energy-recon-grid">
                      <div><span className="text-muted">Billing periods:</span> <strong>{vs.reconciliation.n_billing_periods}</strong></div>
                      <div><span className="text-muted">Months prorated:</span> <strong>{vs.reconciliation.n_months}</strong></div>
                      <div><span className="text-muted">Original total:</span> <strong>{vs.reconciliation.total_original?.toLocaleString()} {key === "electrical" ? "kWh" : "m³"}</strong></div>
                      <div><span className="text-muted">Prorated total:</span> <strong>{vs.reconciliation.total_prorated?.toLocaleString()} {key === "electrical" ? "kWh" : "m³"}</strong></div>
                      <div>
                        <span className="text-muted">Lossless:</span>{" "}
                        <span className={`badge ${vs.reconciliation.is_lossless ? "badge-success" : "badge-danger"}`}>
                          {vs.reconciliation.is_lossless ? "Yes" : `No (diff: ${vs.reconciliation.difference})`}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* n_months flag */}
                {vs.n_months && vs.n_months < 12 && vs.status === "processed" && (
                  <div className="alert alert-info" style={{ marginTop: "8px", fontSize: "0.85rem" }}>
                    <AlertCircle size={16} />
                    <span>Data covers {vs.n_months} months (less than 12). Missing months will not be interpolated.</span>
                  </div>
                )}

                {/* Parse warnings */}
                {vs.parse_warnings && vs.parse_warnings.length > 0 && (
                  <div className="alert alert-warning" style={{ marginTop: "8px", fontSize: "0.8rem" }}>
                    <AlertCircle size={14} />
                    <div>{vs.parse_warnings.map((w, i) => <div key={i}>{w}</div>)}</div>
                  </div>
                )}

                {/* Monthly data preview */}
                {vs.status === "processed" && detail?.parsed_monthly && (
                  <div className="energy-monthly-preview" data-testid={`monthly-preview-${key}`}>
                    <h4>Monthly Data ({detail.parsed_monthly.length} months)</h4>
                    <div className="table-responsive" style={{ maxHeight: "240px", overflow: "auto" }}>
                      <table className="data-table" style={{ fontSize: "0.8rem" }}>
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>Month</th>
                            {key === "electrical" && <><th>kWh (prorated)</th><th>kW Demand</th></>}
                            {key === "gas" && <th>m³ (prorated)</th>}
                            {key === "thermal" && <th>Theoretical Value</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {detail.parsed_monthly.map((m, i) => (
                            <tr key={i}>
                              <td>{m.year}</td>
                              <td>{m.month}</td>
                              {key === "electrical" && <><td>{m.value_prorated?.toLocaleString()}</td><td>{m.kw_demand ?? "—"}</td></>}
                              {key === "gas" && <td>{m.value_prorated?.toLocaleString()}</td>}
                              {key === "thermal" && <td>{m.value_theoretical?.toLocaleString()}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Delete button */}
                {vs.status !== "not_uploaded" && (
                  <button className="btn btn-danger btn-sm" style={{ marginTop: "12px" }} onClick={() => handleDelete(key)} data-testid={`energy-delete-${key}`}>
                    <Trash2 size={14} /> Remove Upload
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EnergyDataUploadView;
